import { useEffect, useState } from "preact/hooks";

import { queueLength, subscribeQueue } from "../offline/queue";

/** How long "All caught up" stays up after a successful replay. */
export const CAUGHT_UP_MS = 3000;

export const OFFLINE_NOTHING_QUEUED_COPY =
  "You're offline — taps will be saved and sent when you're back online.";
export const CAUGHT_UP_COPY = "All caught up";

/** "1 tap saved on this phone — …" / "3 taps saved on this phone — …". */
export function offlineQueuedCopy(count: number): string {
  if (count <= 0) return OFFLINE_NOTHING_QUEUED_COPY;
  return `${count} ${count === 1 ? "tap" : "taps"} saved on this phone — will send when back online.`;
}

/**
 * UX review (done-feedback): the banner used to say "Saved — will sync"
 * whether or not anything was actually waiting. It now reads the real
 * offline queue (`offline/queue.ts`'s `queueLength` + `subscribeQueue`):
 * nothing queued, N taps queued, and — once a replay has sent everything
 * — a brief "All caught up" after the device is back online.
 *
 * `visible` is unchanged (`app.tsx`: offline and not needing reauth). The
 * "All caught up" confirmation renders with its own class so
 * `.offline-banner` still means "offline right now".
 */
export function OfflineBanner({ visible }: { visible: boolean }) {
  const [count, setCount] = useState(0);
  const [caughtUp, setCaughtUp] = useState(false);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    void queueLength().then((n) => {
      if (alive) setCount(n);
    });
    const unsubscribe = subscribeQueue(({ length, replayed }) => {
      if (!alive) return;
      setCount(length);
      if (replayed) {
        if (timer) clearTimeout(timer);
        setCaughtUp(true);
        timer = setTimeout(() => setCaughtUp(false), CAUGHT_UP_MS);
      }
    });
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, []);

  // One persistent live region whose CONTENT changes (offline copy ->
  // "All caught up" -> nothing), so each change is announced reliably;
  // a freshly-inserted live region often isn't.
  return (
    <div class="banner-live-region" role="status" aria-live="polite">
      {visible ? (
        <div class="offline-banner">
          <span class="offline-icon" aria-hidden="true">
            ⟳
          </span>
          <span>{offlineQueuedCopy(count)}</span>
        </div>
      ) : caughtUp ? (
        <div class="caught-up-banner">
          <span class="offline-icon" aria-hidden="true">
            ✓
          </span>
          <span>{CAUGHT_UP_COPY}</span>
        </div>
      ) : null}
    </div>
  );
}
